import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    InputAdornment,
    CircularProgress,
    Select,
    MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { format } from 'date-fns';

// Local Components
import TaskTabToggle from './TabToggle/TaskTabToggle';
import ReviewCard from './ReviewCard';
import EmptyState from './EmptyState';
import ReviewSubmissionModal from './ReviewSubmissionModal';

// Hooks
import {
    useGetProjectTrackerStaff,
    useGetToReviews,
    useGetCompletedToReviews
} from 'services/api/task-management/trackerHooks';

// CARDS_PER_PAGE imported from shared constants
import { CARDS_PER_PAGE } from 'module/task-management/constants';

const ToReviewTab = ({ onPaginationChange }) => {
    // --- State ---
    const [activeTab, setActiveTab] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [pendingPage, setPendingPage] = useState(0);
    const [completedPage, setCompletedPage] = useState(0);
    const [selectedReview, setSelectedReview] = useState(null);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    // rows per page fixed by design (three project cards per page)
    const rowsPerPage = CARDS_PER_PAGE
    const [selectedTask, setSelectedTask] = useState(null);
    const [clickedTaskId, setClickedTaskId] = useState(null);

    const hasValidTaskRelations = (task) => {
        return !!task?.id && !!task?.assigned_to && !!task?.project;
    };

    const hasValidCompletedReviewRelations = (review) => {
        return !!review?.id && hasValidTaskRelations(review?.task);
    };

    // --- API Calls ---
    const { data: toReviewData, isLoading: isLoadingReviews, error: reviewsError } = useGetToReviews();
    const { data: completedToReviewData, isLoading: isLoadingCompletedReviews } = useGetCompletedToReviews();
    const { data: staffData } = useGetProjectTrackerStaff();

    // --- Track completed task IDs to filter from pending ---
    const completedReviewTaskIds = useMemo(() => {
        if (!completedToReviewData?.data?.completed_reviews) return new Set();
        return new Set(
            completedToReviewData.data.completed_reviews
                .filter(hasValidCompletedReviewRelations)
                .map((review) => review.task.id)
        );
    }, [completedToReviewData]);

    // --- 1. Pending Reviews Processing ---

    // Transform raw API data
    const pendingReviewsBase = useMemo(() => {
        if (!toReviewData?.data?.to_review) return [];
        return toReviewData.data.to_review
            .filter(hasValidTaskRelations)
            // .filter(task => !completedReviewTaskIds.has(task.id))
            .filter(task => task.status === 'submitted_for_review')
            .map(task => {
                const latestSubmission = task.submissions?.[0] || null;

                return {
                    id: task.id,
                    employeeName: task.assigned_to.name,
                    employeeAvatar: task.assigned_to.profile_image_url || null,
                    projectName: task.project.name,
                    projectId: task.project_id,
                    rawDate: task.submitted_at || task.created_at, // Use for sorting
                    tasks: [{
                        name: task.name,
                        count: parseFloat(task.value),
                        submittedDate: task.submitted_at ? format(new Date(task.submitted_at), "MMM dd, yyyy 'at' h:mm a") : 'N/A'
                    }],
                    taskDetails: {
                        name: task.name,
                        taskId: task.id,
                        deliverableType: task.project_member_deliverable?.deliverable_type,
                        targetValue: task.project_member_deliverable?.target_value
                    },
                    assignedBy: task.creator?.name || 'Unknown',
                    value: task.value,
                    // Prefer latest submission values, fallback to old fields
                    remarks: latestSubmission?.submission_remarks || task.submission_remarks || '',
                    links: latestSubmission?.submission_link ? [{ text: latestSubmission.submission_link, url: latestSubmission.submission_link }] : (task.submission_link ? [{ text: task.submission_link, url: task.submission_link }] : []),
                    attachments: latestSubmission?.attachments || task.submission_attachments || task.attachments || [],
                    rawData: task
                };
            });
    }, [toReviewData, completedReviewTaskIds]);

    // Filter, Group, and Sort Pending
    const processedPending = useMemo(() => {
        // Filter
        const filtered = pendingReviewsBase.filter(r =>
            r.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.tasks.some(task => task.name?.toLowerCase().includes(searchQuery.toLowerCase()))
        );

        // Deduplicate by task ID
        const seen = new Set();
        const deduplicated = filtered.filter(review => {
            if (seen.has(review.id)) {
                return false;
            }
            seen.add(review.id);
            return true;
        });

        // Group by Employee, then by Project
        const grouped = deduplicated.reduce((acc, review) => {
            const employeeName = review.employeeName;

            if (!acc[employeeName]) {
                acc[employeeName] = {
                    employeeName: review.employeeName,
                    employeeAvatar: review.employeeAvatar,
                    projects: [],
                    ids: [],
                    rawDate: review.rawDate
                };
            }

            // Find or create project group
            let projectGroup = acc[employeeName].projects.find(p => p.projectName === review.projectName);

            if (!projectGroup) {
                projectGroup = {
                    projectName: review.projectName,
                    projectId: review.projectId,
                    tasks: []
                };
                acc[employeeName].projects.push(projectGroup);
            }

            // Add task to project
            projectGroup.tasks.push({
                id: review.id,
                name: review.tasks[0]?.name,
                count: review.tasks[0]?.count,
                submittedDate: review.tasks[0]?.submittedDate,
                rawData: review
            });

            acc[employeeName].ids.push(review.id);
            return acc;
        }, {});

        // Sort
        return Object.values(grouped).sort((a, b) => {
            const dateA = new Date(a.rawDate);
            const dateB = new Date(b.rawDate);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });
    }, [pendingReviewsBase, searchQuery, sortOrder]);

    const paginatedPending = useMemo(() => {
        const start = pendingPage * CARDS_PER_PAGE;
        return processedPending.slice(start, start + CARDS_PER_PAGE);
    }, [processedPending, pendingPage]);

    // to review tab notifications part
    // calculate to review badge count for tasks that have status "to_review"
    const toReviewBadgeCount = useMemo(() => {
        return processedPending.length
    }, [processedPending]
    )

    // --- 2. Completed Reviews Processing ---

    const completedReviewsBase = useMemo(() => {
        if (!completedToReviewData?.data?.completed_reviews) return [];
        return completedToReviewData.data.completed_reviews
            .filter(hasValidCompletedReviewRelations)
            .map(review => {
            const submissions = review?.task?.submissions || [];
            const sortedSubmissions = [...submissions].sort((a, b) =>
                (a.submission_number || 0) - (b.submission_number || 0)
            );

            // Match submission to this specific review by finding the most recent
            // submission that was created at or before the review date
            const reviewDate = new Date(review.reviewed_at);
            const matchedSubmission = sortedSubmissions
                .filter(sub => new Date(sub.created_at) <= reviewDate)
                .pop() || null;

            return {
                id: review.id,
                taskId: review.task.id,
                reviewId: review.id,
                employeeName: review.task.assigned_to.name,
                employeeAvatar: review.task.assigned_to.profile_image_url || null,
                projectName: review.task.project.name,
                projectId: review.task.project_id,
                rawDate: review.reviewed_at,
                tasks: [{
                    id: review.task.id,
                    name: review.task.name,
                    count: parseFloat(review.task.value),
                    reviewedDate: review?.reviewed_at
                        ? format(new Date(review.reviewed_at), "MMM d, yyyy 'at' h:mm a")
                        : 'N/A',
                }],
                rating: review.rating,
                feedback: review.feedback,
                status: 'completed',
                reviewedById: review?.reviewer_id || null,
                remarks: matchedSubmission?.submission_remarks || null,
                submissionLink: matchedSubmission?.submission_link || null,
                attachments: matchedSubmission?.attachments || [],
                rawData: review
            };
        });
    }, [completedToReviewData]);

    const processedCompleted = useMemo(() => {
        const filtered = completedReviewsBase.filter(r =>
            r.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.tasks.some(task => task.name?.toLowerCase().includes(searchQuery.toLowerCase()))
        );

        // Deduplicate by reviewId (unique per review submission, not per task)
        const seen = new Set();
        const deduplicated = filtered.filter(review => {
            if (seen.has(review.reviewId)) {
                return false;
            }
            seen.add(review.reviewId);
            return true;
        });

        // Group by Employee, then by Project
        const grouped = deduplicated.reduce((acc, review) => {
            const employeeName = review.employeeName;

            if (!acc[employeeName]) {
                acc[employeeName] = {
                    employeeName: review.employeeName,
                    employeeAvatar: review.employeeAvatar,
                    projects: [],
                    ids: [],
                    rawDate: review.rawDate
                };
            }

            // Find or create project group
            let projectGroup = acc[employeeName].projects.find(p => p.projectName === review.projectName);

            if (!projectGroup) {
                projectGroup = {
                    projectName: review.projectName,
                    projectId: review.projectId,
                    tasks: []
                };
                acc[employeeName].projects.push(projectGroup);
            }

            // Add each review submission as a separate task entry
            projectGroup.tasks.push({
                id: review.reviewId,
                taskId: review.taskId,
                name: review.tasks[0]?.name,
                count: review.tasks[0]?.count,
                reviewedDate: review.tasks[0]?.reviewedDate,
                rating: review.rating,
                feedback: review.feedback,
                status: review.status,
                rawData: review
            });

            acc[employeeName].ids.push(review.reviewId);
            return acc;
        }, {});

        return Object.values(grouped).sort((a, b) => {
            const dateA = new Date(a.rawDate);
            const dateB = new Date(b.rawDate);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });
    }, [completedReviewsBase, searchQuery, sortOrder]);

    const paginatedCompleted = useMemo(() => {
        const start = completedPage * CARDS_PER_PAGE;
        return processedCompleted.slice(start, start + CARDS_PER_PAGE);
    }, [processedCompleted, completedPage]);


    // reset pages when filters/search change or data reloads
    useEffect(() => {
        setPendingPage(0);
    }, [searchQuery, sortOrder, toReviewData?.data]);

    useEffect(() => {
        setCompletedPage(0);
    }, [searchQuery, sortOrder, completedToReviewData?.data]);

    // Auto-dismiss success message after 5 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Auto-dismiss error message after 5 seconds
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => {
                setErrorMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);

    // clamp current page when processed lists shrink
    useEffect(() => {
        const maxPendingPage = Math.max(0, Math.ceil(processedPending.length / CARDS_PER_PAGE) - 1);
        if (pendingPage > maxPendingPage) setPendingPage(maxPendingPage);
    }, [processedPending.length, pendingPage]);

    useEffect(() => {
        const maxCompletedPage = Math.max(0, Math.ceil(processedCompleted.length / CARDS_PER_PAGE) - 1);
        if (completedPage > maxCompletedPage) setCompletedPage(maxCompletedPage);
    }, [processedCompleted.length, completedPage]);


    // --- Handlers ---

    // --- Pagination handlers ---
    const handlePendingPageChange = (event, newPage) => {
        setPendingPage(newPage)
    }

    const handleCompletedPageChange = (event, newPage) => {
        setCompletedPage(newPage)
    }

    // expose pagination control to parent so it can render full-width sticky pagination
    useEffect(() => {
        if (typeof onPaginationChange === 'function') {
            onPaginationChange({
                page: activeTab === 'pending' ? pendingPage : completedPage,
                rowsPerPage,
                totalCount: activeTab === 'pending' ? processedPending.length : processedCompleted.length,
                onPageChange: (e, newPage) => {
                    if (activeTab === 'pending') setPendingPage(newPage)
                    else setCompletedPage(newPage)
                },
                activeTab
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingPage, completedPage, rowsPerPage, processedPending.length, processedCompleted.length, activeTab, onPaginationChange]);

    const handleReviewClick = (reviewData, taskId) => {
        setSelectedReview(reviewData);
        setClickedTaskId(taskId);
        setIsReviewModalOpen(true);
    };

    const handleReviewSubmit = (task) => {
        setSelectedTask(task);
        setIsReviewModalOpen(false);
    };

    if (isLoadingReviews || isLoadingCompletedReviews) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (reviewsError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography color="error">Failed to load reviews. Please refresh the page.</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box>
                <TaskTabToggle
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'completed', label: 'Completed' }
                    ]}
                />

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={3} mt={2}>
                    <Box>
                        <Typography variant="h3" fontWeight={500} color="#000000">
                            {activeTab === 'pending' ? 'Pending Review' : 'Completed Review'}
                        </Typography>
                        <Typography variant="caption" color="#000000">
                            {activeTab === 'pending' ? 'Submissions awaiting your review' : "Reviews you have completed."}
                        </Typography>
                    </Box>

                    <Box display="flex" gap={2}>

                        {/* Sort order select */}
                        <Select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            size="small"
                            style={{ outline: 'none', outlineStyle: 'none' }}
                            sx={{
                                borderRadius: '50px',
                                minWidth: '180px',
                                backgroundColor: '#f9f9f9',
                                outline: 'none !important',
                                outlineStyle: 'none !important',
                                outlineWidth: '0 !important',
                                '& .MuiSelect-select': {
                                    outline: 'none !important',
                                    outlineStyle: 'none !important',
                                    '&:focus': {
                                        outline: 'none !important',
                                        outlineStyle: 'none !important',
                                        backgroundColor: '#f9f9f9'
                                    }
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#8A8A8A !important',
                                    outline: 'none !important'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#8A8A8A !important',
                                    outline: 'none !important'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#8A8A8A !important',
                                    borderWidth: '1px !important',
                                    outline: 'none !important'
                                },
                                '&.Mui-focused': {
                                    boxShadow: 'none !important',
                                    outline: 'none !important',
                                    outlineStyle: 'none !important',
                                    outlineWidth: '0 !important',
                                    outlineColor: 'transparent !important'
                                },
                                '&:focus': {
                                    outline: 'none !important',
                                    outlineStyle: 'none !important'
                                },
                                '&:focus-visible': {
                                    outline: 'none !important',
                                    outlineStyle: 'none !important'
                                },
                                '&:focus-within': {
                                    outline: 'none !important',
                                    outlineStyle: 'none !important'
                                }
                            }}
                            renderValue={(value) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarMonthIcon sx={{ fontSize: 20 }} />
                                    {value === 'newest' ? 'Newest First' : 'Oldest First'}
                                </Box>
                            )}
                        >
                            <MenuItem value="newest">Newest First</MenuItem>
                            <MenuItem value="oldest">Oldest First</MenuItem>
                        </Select>

                        {/* Search projects field */}
                        <TextField
                            placeholder="Search projects or tasks"
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ outline: 'none', outlineStyle: 'none' }}
                            sx={{
                                width: '250px',
                                outline: 'none !important',
                                outlineStyle: 'none !important',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '100px',
                                    outline: 'none !important',
                                    outlineStyle: 'none !important',
                                    outlineWidth: '0 !important',
                                    '& fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        borderWidth: '1px !important',
                                        outline: 'none !important'
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: 'none !important',
                                        outline: 'none !important',
                                        outlineStyle: 'none !important',
                                        outlineWidth: '0 !important',
                                        outlineColor: 'transparent !important'
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important'
                                    }
                                },
                                '& input': {
                                    outline: 'none !important',
                                    '&:focus': {
                                        outline: 'none !important',
                                        boxShadow: 'none !important'
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important'
                                    }
                                },
                                '&:focus-within': {
                                    outline: 'none !important'
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                style: { outline: 'none', outlineStyle: 'none' }
                            }}
                        />

                    </Box>
                </Box>

                {successMessage && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor: '#e0fce8',
                        border: '1px solid #e0fce8',
                        borderLeft: '4px solid #27ae60',
                        borderRadius: 1,
                        px: 2,
                        py: 1.5,
                        mb: 2
                    }}>
                        <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#28A745',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.5 6.79L1.71 4L0.75 4.95L4.5 8.7L11.25 1.95L10.295 1L4.5 6.79Z" fill="white" />
                            </svg>
                        </Box>
                        <Typography sx={{ fontSize: '14px', color: '#155724', fontWeight: 500 }}>
                            {successMessage}
                            {' '}
                            <Box
                                component="span"
                                onClick={() => setActiveTab('completed')}
                                sx={{
                                    color: '#2F80ED',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    '&:hover': {
                                        color: '#2F80ED'
                                    }
                                }}
                            >
                                View your completed reviews here.
                            </Box>
                        </Typography>
                    </Box>
                )}

                <Grid container spacing={3}>
                    {activeTab === 'pending' ? (
                        paginatedPending.length > 0 ? (
                            paginatedPending.map((group) => (
                                <Grid item xs={12} md={6} lg={4} key={group.employeeName}>
                                    <ReviewCard reviewData={group} onReviewClick={handleReviewClick} isCompletedTab={false} />
                                </Grid>
                            ))
                        ) : <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            width="100%"
                            minHeight="300px" // Adjust height as needed
                        >
                            <EmptyState type="pending" />
                        </Box>
                    ) : (
                        paginatedCompleted.length > 0 ? (
                            paginatedCompleted.map((group) => (
                                <Grid item xs={12} md={6} lg={4} key={group.employeeName}>
                                    <ReviewCard reviewData={group} onReviewClick={handleReviewClick} isCompletedTab={true} completedReviews={completedReviewsBase} />
                                </Grid>
                            ))
                        ) : <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            width="100%"
                            minHeight="300px"
                        >
                            <EmptyState type="completed" />
                        </Box>
                    )}
                </Grid>

            </Box>

            <ReviewSubmissionModal
                open={isReviewModalOpen}
                onClose={() => setIsReviewModalOpen(false)}
                reviewData={selectedReview}
                selectedTask={selectedReview?.selectedTask}
                onSubmit={handleReviewSubmit}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
            />
        </>
    );
};

export default ToReviewTab;
